import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  CheckCircle2, Loader2, Crown, XCircle, ArrowRight, Sparkles,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';
import axios from 'axios';
import SEO from '@/components/SEO';
import { PLAN_PRICES } from '@/lib/plans';
import { trackEvent } from '@/lib/metaPixel';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed, expired
  const [message, setMessage] = useState('Verifying your payment...');
  const [planType, setPlanType] = useState(null); // 'basic' | 'pro'
  const { getAuthHeader, refreshUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      setMessage('No payment session found. Please try again.');
      return;
    }

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    let cancelled = false;

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 15;
      const pollInterval = 2000;

      if (cancelled) return;

      if (attempts >= maxAttempts) {
        setStatus('failed');
        setMessage(
          'Payment verification is taking longer than expected. Your subscription may still activate shortly — please check your email or refresh this page in a minute.'
        );
        return;
      }

      try {
        const response = await axios.get(
          `${API}/api/payments/status/${sessionId}`,
          { headers: getAuthHeader() }
        );

        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          const plan = data.is_pro ? 'pro' : data.is_basic ? 'basic' : null;
          setPlanType(plan);
          if (plan) {
            const value = PLAN_PRICES[plan]?.gbp;
            trackEvent('Subscribe', { value, currency: 'GBP', predicted_ltv: value });
          }
          setMessage(
            plan === 'pro'
              ? 'Payment successful! You now have Pro access.'
              : plan === 'basic'
              ? 'Payment successful! You now have Basic access.'
              : 'Payment successful! Your subscription is active.'
          );
          await refreshUser();
          toast.success(`Welcome to Poettik ${plan === 'pro' ? 'Pro' : 'Basic'}!`);
          return;
        }

        if (data.status === 'expired') {
          setStatus('expired');
          setMessage('Payment session expired. Please try again.');
          return;
        }

        setMessage('Payment is being processed...');
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        // Keep retrying on transient errors
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus('failed');
          setMessage(
            'We could not confirm your payment automatically. If you were charged, please refresh this page in a minute or contact hey@poettik.com.'
          );
        }
      }
    };

    pollPaymentStatus();

    return () => {
      cancelled = true;
    };
  }, [sessionId, getAuthHeader, refreshUser, isAuthenticated, navigate]);

  const planLabel = planType === 'pro' ? 'Pro' : 'Basic';
  const planFeatures = planType === 'pro'
    ? [
        '15 generations per day',
        '5 text hooks per product',
        'Re-spin hooks & scripts for fresh ideas',
        'Export all content',
        'Priority AI processing',
      ]
    : [
        '5 generations per day',
        '3 text hooks per product',
        'Unlimited saved ideas',
        '15-60 second scripts',
        'Cancel anytime',
      ];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <SEO title="Payment Confirmed" description="Your Poettik subscription is now active." noindex />
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-20 -left-40 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <Card variant="glass" className="relative z-10 max-w-md w-full p-8 text-center" data-testid="payment-status-card">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <Logo size="lg" />
        </Link>

        <div className="mb-6">
          {status === 'checking' && (
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto animate-pulse">
              <Loader2 className="w-10 h-10 text-primary animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="w-20 h-20 rounded-full bg-success/20 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-success" />
            </div>
          )}
          {(status === 'failed' || status === 'expired') && (
            <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-destructive" />
            </div>
          )}
        </div>

        <h1 className="font-display text-2xl font-bold mb-2" data-testid="payment-status-title">
          {status === 'checking' && 'Verifying Payment'}
          {status === 'success' && `Welcome to ${planLabel}!`}
          {status === 'failed' && 'Payment Issue'}
          {status === 'expired' && 'Session Expired'}
        </h1>

        <p className="text-muted-foreground mb-8" data-testid="payment-status-message">
          {message}
        </p>

        {status === 'success' && (
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent">
              {planType === 'pro' ? <Crown className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              <span className="text-sm font-medium">{planLabel} Member</span>
            </div>

            <div className="text-sm text-muted-foreground mb-6 text-left mx-auto max-w-xs">
              <p className="mb-2">You now have access to:</p>
              <ul className="space-y-1">
                {planFeatures.map((f) => (
                  <li key={f}>✓ {f}</li>
                ))}
              </ul>
            </div>

            <Link to="/profile?welcome=1">
              <Button variant="premium" size="lg" className="w-full" data-testid="go-to-profile-btn">
                Complete your profile
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground">
              Takes 30 seconds — tells the AI a little about you so your scripts match how you actually talk.
            </p>
          </div>
        )}

        {(status === 'failed' || status === 'expired') && (
          <div className="space-y-4">
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={() => window.location.reload()}
              data-testid="retry-verification-btn"
            >
              Refresh & try again
            </Button>
            <Link to="/pricing">
              <Button variant="ghost" size="lg" className="w-full" data-testid="back-to-pricing-btn">
                Back to Pricing
              </Button>
            </Link>
            <Link to="/support">
              <Button variant="ghost" size="sm" className="w-full">
                Contact Support
              </Button>
            </Link>
          </div>
        )}

        {status === 'checking' && (
          <p className="text-sm text-muted-foreground">
            Please wait while we confirm your payment...
          </p>
        )}
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
